import {
  signInWithEmailAndPassword,
  updatePassword,
  signInWithPopup,
  confirmPasswordReset,
  verifyPasswordResetCode,
  signOut,
  GoogleAuthProvider
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";

import { auth, functions } from "../firebase/firebase"; // Use your shared firebase.js instance
import { collection, query, where, getDocs, updateDoc, doc } from "firebase/firestore";
import { db } from "../firebase/firebase";  // Correct the import path if needed
import bcrypt from 'bcryptjs';

// Sign In with Email
const signInWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    throw error;
  }
};

// Sign In with Google
const signInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    throw error;
  }
};

const normalizePhonePassword = (value) => String(value || "").trim().replace(/\D/g, "");

// Create or sync an Auth user without switching the currently signed-in user.
const createUser = async (email, password, options = {}) => {
  try {
    const phonePassword = normalizePhonePassword(password);
    if (phonePassword.length !== 11) {
      throw new Error("Enter the user's complete 11-digit phone number. It will be used as the temporary password.");
    }
    const ensureAuthUser = httpsCallable(functions, "ensureAuthUser");
    const result = await ensureAuthUser({
      email,
      password: phonePassword,
      displayName: options.displayName || "",
      hospitalId: options.hospitalId || "",
      disabled: !!options.disabled,
    });
    return result.data;
  } catch (error) {
    console.error("Error creating user:", error.message);
    throw error;
  }
};

const formatAuthError = (error, fallbackMessage) => {
  const code = error?.code;
  switch (code) {
    case "auth/invalid-email":
      return "Enter a valid email address.";
    case "auth/user-disabled":
      return "This user account has been disabled.";
    case "auth/user-not-found":
      return "If an account exists for this email, a reset link has been sent.";
    case "auth/expired-action-code":
      return "This reset link has expired. Please request a new one.";
    case "auth/invalid-action-code":
      return "This reset link is invalid. Please request a new one.";
    case "auth/weak-password":
      return "Password should be at least 6 characters.";
    case "auth/too-many-requests":
      return "Too many attempts. Please try again later.";
    case "auth/invalid-continue-uri":
      return "Password reset link configuration is invalid. Please contact support.";
    case "auth/unauthorized-continue-uri":
      return "This app domain is not authorized for password reset links. Please contact support.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    default:
      return fallbackMessage || "Something went wrong. Please try again.";
  }
};

// Reset password (send reset email)
const resetPassword = async (email, actionCodeSettings) => {
  try {
    const requestPasswordReset = httpsCallable(functions, "requestPasswordReset");
    const fallbackUrl =
      typeof window !== "undefined" && window.location?.origin
        ? `${window.location.origin}/set-new-password`
        : "https://clearhealthemr.web.app/set-new-password";
    await requestPasswordReset({
      email,
      continueUrl: actionCodeSettings?.url || fallbackUrl,
      appName: "ClearHealth EMR",
    });
    return { success: true, message: "If an account exists for this email, a reset link has been sent." };
  } catch (error) {
    const message = formatAuthError(error, "Failed to send password reset email.");
    if (error?.code === "auth/user-not-found") {
      return { success: true, message };
    }
    return { success: false, error: message };
  }
};

// Validate reset code and resolve email
const verifyResetCode = async (oobCode) => {
  try {
    const email = await verifyPasswordResetCode(auth, oobCode);
    return { success: true, email };
  } catch (error) {
    return { success: false, error: formatAuthError(error, "Failed to validate reset link.") };
  }
};

// Confirm password reset (complete flow)
const resetUserPassword = async (oobCode, newPassword, email) => {
  try {
    await confirmPasswordReset(auth, oobCode, newPassword);
    if (email) {
      try {
        const normalizedEmail = email.trim().toLowerCase();
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("email", "==", normalizedEmail));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const userDoc = querySnapshot.docs[0];
          const userRef = doc(db, "users", userDoc.id);
          const hashedPassword = bcrypt.hashSync(newPassword, 10);

          const nextUserData = {
            ...userDoc.data(),
            id: userDoc.id,
            password: hashedPassword,
            needsPasswordChange: false,
          };

          await updateDoc(userRef, {
            password: hashedPassword,
            needsPasswordChange: false,
          });

          sessionStorage.setItem("userData", JSON.stringify(nextUserData));
          return { success: true, user: nextUserData };
        }
      } catch (firestoreError) {
        console.error("Failed to sync password to Firestore:", firestoreError);
      }
    }
    return { success: true };
  } catch (error) {
    return { success: false, error: formatAuthError(error, "Failed to reset password.") };
  }
};

// Logout
const logoutUser = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || "Failed to log out." };
  }
};

// Update User Password + update Firestore user document
const updateUserPassword = async (newPassword) => {
  const user = auth.currentUser;

  if (!user) {
    return { success: false, error: "User is not logged in." };
  }

  try {
    // 1. Update password in Firebase Auth
    await updatePassword(user, newPassword);

    // 2. Find Firestore user document
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", user.email));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      const userRef = doc(db, "users", userDoc.id);

      const hashedPassword = bcrypt.hashSync(newPassword, 10); // hash password before saving

      const nextUserData = {
        ...userDoc.data(),
        id: userDoc.id,
        password: hashedPassword,
        needsPasswordChange: false,
      };

      await updateDoc(userRef, {
        password: hashedPassword,
        needsPasswordChange: false,
      });

      sessionStorage.setItem("userData", JSON.stringify(nextUserData));
      return { success: true, user: nextUserData };
    } else {
      return { success: false, error: "User not found in Firestore." };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export {
  signInWithEmail,
  signInWithGoogle,
  resetPassword,
  verifyResetCode,
  resetUserPassword,
  logoutUser,
  createUser,
  updateUserPassword
};
